import { ConnectedWallet } from '@privy-io/react-auth';
import { ConnectedStandardSolanaWallet } from '@privy-io/react-auth/solana';
import { type ComponentType, type CSSProperties, type SVGProps } from 'react';

import { solana } from '@/constants/chain';

import { MatchaConnectedWallet } from '@/hooks/useMatchaWallets';

import { classNames } from '@/utils/classnames';

import * as evm from '../../../../public/images/chains/evm.svg';
import { Avatar, AvatarProps } from '../Avatar';
import { getAvatarBadgeStyles } from '../Avatar/utils';
import { chainAvatarSvg } from '../ChainAvatar/chainAvatarLogos';

type WalletAvatarProps = AvatarProps & {
  /**
   * Wallet to be shown.
   */
  wallet:
    | ConnectedWallet
    | ConnectedStandardSolanaWallet
    | MatchaConnectedWallet<ConnectedWallet>
    | MatchaConnectedWallet<ConnectedStandardSolanaWallet>;
  /**
   * Custom style for the component.
   */
  style?: CSSProperties & { WebkitMaskMode?: string };
};

export const WalletAvatar = (props: WalletAvatarProps) => {
  const { wallet, size = 32, className, style, ...otherProps } = props;

  const WalletIcon: string | ComponentType<SVGProps<SVGSVGElement>> | undefined =
    'standardWallet' in wallet ? wallet.standardWallet.icon : wallet.meta.icon;
  const chainTypeSrc = 'standardWallet' in wallet ? chainAvatarSvg[solana.id] : evm.default.src;

  const chainBadgeStyles = getAvatarBadgeStyles(size, 'bottom-right');

  // Apply any stack mask only to the base wallet image so the outer shadow and chain badge are not clipped.
  const { WebkitMaskImage, maskImage, WebkitMaskMode, maskMode, ...rootStyle } = style ?? {};
  const stackMaskStyle = { WebkitMaskImage, maskImage, WebkitMaskMode, maskMode };

  return (
    <div
      className={classNames(
        'relative inline-flex drop-shadow-[var(--token-shadow-15)] shrink-0',
        className,
      )}
      style={rootStyle}
    >
      <div className="flex" style={stackMaskStyle}>
        <div className="flex" style={chainBadgeStyles.maskStyle}>
          {typeof WalletIcon === 'string' || WalletIcon == null ? (
            <Avatar src={WalletIcon?.trim()} size={size} className="shadow-none!" {...otherProps} />
          ) : (
            <WalletIcon
              width={size}
              height={size}
              style={{ borderRadius: otherProps.radius ?? '100%' }}
              role={otherProps.alt ? 'img' : undefined}
              aria-label={otherProps.alt || undefined}
              aria-hidden={!otherProps.alt}
            />
          )}
        </div>
      </div>
      <Avatar src={chainTypeSrc} radius="32%" {...chainBadgeStyles.badgeProps} />
    </div>
  );
};
